/* Automatic profiling after a harvest, per connection: one read and two writes.
 *
 * **All `GET`/`PUT`, and none of them takes a key.** Every resource here has its natural key in the
 * URL and its whole state in the body — one boolean, or nothing at all — so a `PUT` is idempotent
 * by construction. `shared/api/http.ts`'s `put` deliberately takes no key parameter, and the
 * platform's `post-idempotency-key` gate has nothing to enforce on these.
 *
 * Each mutation invalidates its own read **and** the `audit` namespace, because switching this on
 * or off lands in the chain and the connection page renders its history from it — the defect §22
 * records for the score listing, where a hand-listed invalidation set never reached the panel that
 * had gone stale.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { AutoProfile } from '@/shared/api/generated/catalog';
import {
  isAutoProfile as isAutoProfileEnvelope,
  isAutoProfileQueueCounts,
} from '@/shared/api/generated/catalog';
import { getOne, put } from '@/shared/api/http';

/* The generated guard checks that `queue` is present and not what is in it, so a payload missing a
   count would pass and the card would crash on `undefined.toLocaleString()` — a blank page where
   "could not be read" was the honest answer. Checked here, once, for every read and write. */
function isAutoProfile(value: unknown): value is AutoProfile {
  return isAutoProfileEnvelope(value) && isAutoProfileQueueCounts(value.queue);
}

/* Keyed under `catalog` rather than a namespace of its own, so invalidating `['catalog']` after a
   harvest — which the connection mutations already do — also refreshes a queue count that the
   harvest has just changed. A separate namespace would put this cache outside every existing
   invalidation and make it the one panel that never updates. */
export const AUTO_PROFILE_KEY = ['catalog', 'auto-profile'] as const;

function autoProfileUrl(connectionId: string): string {
  return `/v1/catalog/connections/${encodeURIComponent(connectionId)}/auto-profile`;
}

/** One source's automatic-profiling setting, and how much profiling work is outstanding for it.
 *
 * **A 200 with `configured: false` is the ordinary answer, not an error**, so there is no branch
 * here for it — the component reads `configured`. That is the API's choice and the right one: a
 * 404 would be indistinguishable from "no such connection", and the two send a reader to different
 * places.
 */
export function useAutoProfile(connectionId: string): UseQueryResult<AutoProfile> {
  return useQuery({
    queryKey: [...AUTO_PROFILE_KEY, connectionId],
    queryFn: () =>
      getOne(autoProfileUrl(connectionId), isAutoProfile, 'automatic profiling'),
  });
}

export interface AutoProfileEnabledRequest {
  readonly connectionId: string;
  readonly enabled: boolean;
}

/** Switch automatic profiling on or off for one source.
 *
 * The server authorizes this on `catalog.dataset:profile` rather than on the permission to harvest,
 * so a reader who may look at the setting can be refused the change — which is why the card renders
 * a 403 here as a read-only state rather than as an error.
 */
export function useSetAutoProfileEnabled(): UseMutationResult<
  AutoProfile,
  Error,
  AutoProfileEnabledRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, enabled }: AutoProfileEnabledRequest) =>
      put(
        `${autoProfileUrl(connectionId)}/enabled`,
        isAutoProfile,
        'automatic profiling',
        {
          enabled,
        },
      ),
    onSuccess: (_profile, { connectionId }) => {
      void client.invalidateQueries({ queryKey: [...AUTO_PROFILE_KEY, connectionId] });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/** Put this source's exhausted profiling work back in the queue.
 *
 * No body: the request carries no state a caller chooses, and its whole content is "these, again".
 */
export function useRetryFailedAutoProfiles(): UseMutationResult<
  AutoProfile,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      put(
        `${autoProfileUrl(connectionId)}/retry-failed`,
        isAutoProfile,
        'automatic profiling',
        {},
      ),
    onSuccess: (_profile, connectionId) => {
      void client.invalidateQueries({ queryKey: [...AUTO_PROFILE_KEY, connectionId] });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}
